<script>
import { Log } from '@artup/utilities'
import { FormMixin } from '@artup/client'
import ViewMixin from '../home/views/view'

export default {
  mixins: [
    FormMixin,
    ViewMixin
  ],

  data () {
    return {
      // Indicates that the account statistics are being loaded
      isLoading: false,
      // Error which occurred while loading the account statistics
      error: null
    }
  },

  computed: {
    // All accounts except the integration accounts, which are technical and managed manually
    allAccounts () {
      return (this.accounts.accounts || []).filter(account => !account.isIntegrationAccount)
    },

    // Accounts whose identity has been legally verified
    verifiedAccounts () {
      return this.allAccounts.filter(account => account.isVerified)
    },

    // Account statistics to display
    statistics () {
      const { allAccounts, verifiedAccounts } = this
      return [
        { label: 'Accounts', value: allAccounts.length, color: 'indigo-7' },
        { label: 'Verified', value: verifiedAccounts.length, color: 'green-7' },
        { label: 'Not verified', value: allAccounts.length - verifiedAccounts.length, color: 'orange-7' }
      ]
    }
  },

  methods: {
    // Loads the accounts, for the statistics
    async populate () {
      this.isLoading = true
      this.error = null

      try {
        await this.accounts.loadAccounts()
      } catch (error) {
        Log.exception(error)
        this.error = error
      } finally {
        this.isLoading = false
      }
    }
  },

  async created () {
    await this.populate()
  }
}

</script>

<template>
  <main class="artup-account-view administration-welcome">
    <div class="text-h5 q-mb-md">
      Welcome, {{ account.fullName }}
    </div>
    <div v-if="account.lastLogin">
      Your last login was on {{ datetime(account.lastLogin) }}
    </div>

    <section class="statistics q-mt-xl">
      <div class="row items-center q-mb-md">
        <span class="title">
          Accounts
        </span>
        <q-btn icon="refresh" flat round dense color="grey-7" class="q-ml-sm" :loading="isLoading" @click="populate()">
          <q-tooltip>
            Refresh
          </q-tooltip>
        </q-btn>
      </div>

      <div v-if="error" class="text-negative q-mb-md">
        Account statistics could not be loaded. {{ error.message }}
      </div>

      <div class="tiles">
        <div v-for="item in statistics" :key="item.label" class="tile">
          <span class="value" :class="`text-${item.color}`">
            {{ isLoading ? '–' : item.value }}
          </span>
          <span class="label">
            {{ item.label }}
          </span>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
@import "../home/views/view.scss";

.statistics {
  .title {
    font-size: 16px;
    font-weight: 500;
    color: #616161;
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(3, minmax(140px, 1fr));
    gap: 16px;
    max-width: 600px;

    .tile {
      display: flex;
      flex-direction: column;
      padding: 16px 20px;
      border: solid #0000001f 1px;
      border-radius: 5px;
      background-color: #fafafa;

      .value {
        font-size: 32px;
        font-weight: 500;
        line-height: 1.1;
      }

      .label {
        margin-top: 6px;
        font-size: 14px;
        color: #424242;
      }
    }
  }
}

/* Layout adjustments for mobile screens */
@media screen and (max-width: 640px) {
  .statistics {
    .tiles {
      grid-template-columns: 1fr;
    }
  }
}
</style>
